import {
  ChangeDetectionStrategy,
  Component,
  inject,
  NgZone,
  signal,
} from '@angular/core';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { Toast, ToastPackage, ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { BatchDocumentsService } from '../../batch-documents.service';
import { BatchDocumentToastPayload } from '../batch-document-toast.type';

@Component({
  selector: '[batch-document-toast]',
  standalone: true,
  templateUrl: './batch-document-toast.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('flyInOut', [
      state('inactive', style({ opacity: 0 })),
      state('active', style({ opacity: 1 })),
      state('removed', style({ opacity: 0 })),
      transition(
        'inactive => active',
        animate('{{ easeTime }}ms {{ easing }}')
      ),
      transition('active => removed', animate('{{ easeTime }}ms {{ easing }}')),
    ]),
  ],
  host: {
    '[class]': 'toastClasses',
    '[@flyInOut]': '_state',
    '[style.display]': 'displayStyle',
    '(mouseenter)': 'stickAround()',
    '(mouseleave)': 'delayedHideToast()',
  },
})
export class BatchDocumentToastComponent extends Toast<BatchDocumentToastPayload> {
  private readonly batchDocumentsService = inject(BatchDocumentsService);
  private readonly translateService = inject(TranslateService);

  protected readonly isDownloading = signal(false);
  protected readonly payload = this.options.payload!;
  protected readonly downloadLabel = this.payload.buttonName;

  constructor() {
    super(inject(ToastrService), inject(ToastPackage), inject(NgZone));
  }

  protected download(event: Event): void {
    event.stopPropagation();

    const documentId = this.payload.documentId;

    if (!documentId || this.isDownloading()) {
      return;
    }

    this.isDownloading.set(true);

    this.batchDocumentsService
      .download(documentId)
      .pipe(take(1))
      .subscribe({
        next: () => this.toastrService.clear(this.toastPackage.toastId),
        error: () => {
          this.isDownloading.set(false);
          this.toastrService.error(
            this.translateService.instant('AN_ERROR_HAS_OCCURRED')
          );
        },
      });
  }

  protected dismissToast(): void {
    this.toastrService.clear(this.toastPackage.toastId);
  }
}
